import type { UUID } from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { Configuration } from '../../request/Types';
import type { AssociationPastRequest, NewVehicleForm } from './Types';

export class FuscaVehiclesRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public createVehicle = async (vehicle?: NewVehicleForm): Promise<NewVehicleForm> => {
    const body = vehicle ? JSON.stringify(decamelizeKeys(vehicle)) : undefined;
    const requestData = {
      endpoint: '/vehicles',
      method: HttpMethod.POST,
      body,
    };

    const result = await this.requester.request(requestData);

    const newResult = {
      ...result.data,
      model: {
        brand: null,
        name: null,
        year: null,
        version: '',
        isSuggestion: false,
      },
    };

    return camelcaseKeys(newResult, { deep: true });
  };

  public deleteVehicle = async (vehicleId: UUID): Promise<void> => {
    const requestData = {
      endpoint: `/vehicles/${vehicleId}`,
      method: HttpMethod.DELETE,
    };

    const res = await this.requester.request(requestData);

    if (res.status !== 204) {
      throw new Error();
    }
  };

  public removeDevice = async (vehicleId: UUID): Promise<void> => {
    const requestData = {
      endpoint: `/vehicles/${vehicleId}/installed-device`,
      method: HttpMethod.DELETE,
    };

    const res = await this.requester.request(requestData);

    if (res.status !== 204) {
      throw new Error();
    }
  };

  public associateDevice = async (vehicleId: UUID, deviceId: string): Promise<void> => {
    const requestData = {
      endpoint: `/vehicles/${vehicleId}/installed-device`,
      method: HttpMethod.POST,
      body: deviceId,
      // The endpoint expects the raw device id as plain text. With the default
      // application/json content type, axios wraps non-JSON-parseable ids
      // (e.g. "00D2066777") in quotes, and the lookup fails with 404.
      headers: { 'Content-type': 'text/plain' },
    };

    const res = await this.requester.request(requestData);

    if (res.status !== 204) {
      throw new Error();
    }
  };

  public createDriverAssociationRequestBatch = async (vehicleId: UUID, associationsRequests: AssociationPastRequest[]): Promise<void> => {
    const requestData = {
      endpoint: `/vehicles/${vehicleId}/driver-past-association/batch`,
      method: HttpMethod.POST,
      body: JSON.stringify(decamelizeKeys(associationsRequests)),
    };

    const res = await this.requester.request(requestData);

    if (res.status !== 204) {
      throw new Error();
    }
  };
}

export default FuscaVehiclesRequester;
